import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Sparkles, Loader2, Save, ArrowLeft, Wand2, AlertTriangle, Layers, Files } from "lucide-react";
import { toast } from "sonner";
import CockpitLayout from "@/components/admin/CockpitLayout";
import ImageManager from "@/components/admin/ImageManager";
import ItemFields from "@/components/admin/ItemFields";
import { LocationSelect } from "@/components/admin/LocationSelect";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { GoldAction } from "@/components/admin/GoldUi";

const GOLD = "#8C5A00", INK = "#111923", SLATE = "#46525F", MUTED = "#46525F", LINE = "#DDDAD4";
const cardStyle = { border: `1px solid ${LINE}`, boxShadow: "0 1px 2px rgba(16,24,32,.04)" };

const empty = {
  title: "", category: "Sonstiges", condition: "Gut", price: null, description: "",
  seo_title: "", seo_description: "", whatsapp_text: "", status: "draft", ai_confidence: 0,
};

const statusFromConfidence = (c) => (c >= 40 ? "draft" : "review");

export default function NewItem() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canPublish = can(user, "item:publish");
  const [images, setImages] = useState([]);
  const [form, setForm] = useState(empty);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [locCtx, setLocCtx] = useState(null);
  const [locationId, setLocationId] = useState("");

  useEffect(() => {
    api.get("/me/locations").then(({ data }) => {
      if (!data.multi) return;
      setLocCtx(data);
      const def = data.active_location_id || (data.locations?.length === 1 ? data.locations[0].id : "");
      setLocationId(def || "");
    }).catch(() => {});
  }, []);

  const mainImage = images.find((i) => i.is_main) || images[0];

  const checkDuplicates = async (title) => {
    if (!title || title.trim().length < 3) { setDuplicates([]); return; }
    try { const { data } = await api.post("/items/check-duplicate", { title }); setDuplicates(data.duplicates || []); }
    catch { /* ignore */ }
  };

  const applyCandidate = (it) => {
    setForm({
      title: it.title || "",
      category: it.category || "Sonstiges",
      condition: it.condition || "Gut",
      price: it.suggested_price ?? null,
      description: it.description || "",
      seo_title: it.seo_title || "",
      seo_description: it.seo_description || "",
      whatsapp_text: it.whatsapp_text || "",
      status: statusFromConfidence(it.confidence || 0),
      ai_confidence: it.confidence || 0,
    });
    checkDuplicates(it.title || "");
  };

  // re-check duplicates as the title changes (debounced)
  useEffect(() => {
    const t = setTimeout(() => checkDuplicates(form.title), 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.title]);

  const analyze = async () => {
    if (!mainImage) { toast.error("Bitte zuerst ein Foto hochladen."); return; }
    setAnalyzing(true);
    try {
      const { data } = await api.post("/ai/analyze", { image_id: mainImage.id });
      setAnalysis(data);
      if (data.items?.length) applyCandidate(data.items[0]);
      (data.warnings || []).forEach((w) => toast.warning(w));
      if (data.detected_count > 1) toast.info(`${data.detected_count} Produkte erkannt – wähle das passende aus.`);
      else toast.success("KI-Analyse fertig!");
    } catch {
      toast.error("KI-Analyse fehlgeschlagen.");
    } finally { setAnalyzing(false); }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Bitte einen Titel angeben."); return; }
    if (locCtx?.multi && !locationId) { toast.error("Bitte einen Standort wählen."); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        ...(locCtx?.multi ? { location_id: locationId } : {}),
        images: images.map((i) => ({ id: i.id, original_path: i.original_path, image_path: i.image_path, thumb_path: i.thumb_path, width: i.width, height: i.height, is_main: i.is_main, quality: i.quality })),
      };
      const { data } = await api.post("/items", payload);
      toast.success("Fundstück gespeichert!");
      navigate(`/dashboard/fundstuecke/${data.id}/bearbeiten`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally { setSaving(false); }
  };

  return (
    <CockpitLayout active="neu" title="Neues Fundstück">
      <Link to="/dashboard/fundstuecke" className="fundio-text-link mb-3"><ArrowLeft size={18} aria-hidden="true" /> Fundstücke</Link>
      <h1 className="fundio-page-title">Neues Fundstück</h1>
      <p className="mt-2 fundio-muted">Starte mit einem Foto. Prüfe danach die Angaben.</p>
      <ol className="fundio-steps mt-5" aria-label="Ablauf der Erfassung">
        {["Foto", "Details prüfen", "Speichern"].map((label, i) => <li key={label}><span className="fundio-step-number">{i + 1}</span>{label}</li>)}
      </ol>

      <form className="fundio-create-grid mt-6" onSubmit={(e) => { e.preventDefault(); if (!saving && !uploading && !analyzing) save(); }}>
        {/* left: images + AI */}
        <div className="fundio-create-photos">
          <div className="fundio-panel">
            <h2 className="fundio-section-title mb-4 flex items-center gap-2"><Layers size={20} aria-hidden="true" /> Fotos</h2>
            <ImageManager images={images} setImages={setImages} onBusyChange={setUploading} locationId={locCtx?.multi ? locationId : undefined} />
            <p className="mt-3 text-sm fundio-muted">Ein klares Foto erleichtert das Prüfen.</p>

          <GoldAction full variant="primary" className="mt-4" data-testid="ai-analyze-btn" onClick={analyze} disabled={analyzing || saving || uploading || images.length === 0}>
            {analyzing ? <Loader2 size={18} className="animate-spin" /> : <Wand2 size={18} />} {analyzing ? "Analysiere …" : "Mit KI analysieren"}
          </GoldAction>
          {images.length === 0 && <p className="mt-2 text-sm fundio-muted">Zuerst ein Foto hinzufügen.</p>}
          </div>

          {analysis && (
            <div className="rounded-xl bg-white p-4" style={cardStyle} data-testid="ai-result">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm font-bold" style={{ color: INK }}><Sparkles size={15} style={{ color: GOLD }} /> KI-Ergebnis</span>
                <span className="text-xs" style={{ color: SLATE }}>{analysis.detected_count} erkannt</span>
              </div>
              {analysis.detected_count > 1 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {analysis.items.map((it, i) => (
                    <button type="button" key={i} onClick={() => applyCandidate(it)} className="fundio-action">
                      {it.title || `Produkt ${i + 1}`} · {it.confidence}%
                    </button>
                  ))}
                </div>
              )}
              {form.ai_confidence > 0 && (
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs" style={{ color: MUTED }}><span>KI-Sicherheit</span><span>{form.ai_confidence}%</span></div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full" style={{ background: "#EFEDE6" }}><div className="h-full rounded-full" style={{ width: `${form.ai_confidence}%`, background: GOLD }} /></div>
                </div>
              )}
              {analysis.warnings?.length > 0 && (
                <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700"><AlertTriangle size={14} className="mt-0.5 shrink-0" /><span>{analysis.warnings.join(" ")}</span></div>
              )}
            </div>
          )}

          {duplicates.length > 0 && (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="duplicate-warning">
              <div className="flex items-center gap-2 text-sm font-bold text-amber-700"><Files size={15} /> Möglicherweise schon vorhanden</div>
              <p className="mt-1 text-xs text-amber-700/80">Ähnliche Fundstücke gefunden – bitte prüfen, ob es ein Duplikat ist.</p>
              <div className="mt-3 space-y-1.5">
                {duplicates.slice(0, 4).map((d) => (
                  <Link key={d.id} to={`/dashboard/fundstuecke/${d.id}/bearbeiten`} target="_blank" className="flex items-center gap-2 rounded-lg bg-white px-2.5 py-1.5 text-xs hover:bg-fundio-subtle" style={{ color: INK }}>
                    {d.thumb ? <img src={fileUrl(d.thumb)} alt="" className="h-7 w-7 rounded object-cover" /> : <span className="h-7 w-7 rounded" style={{ background: "#EFEDE6" }} />}
                    <span className="truncate">{d.title}</span>
                    <span className="ml-auto shrink-0" style={{ color: SLATE }}>{d.category}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* right: form */}
        <div className="fundio-panel">
          <h2 className="fundio-section-title mb-4">Details</h2>
          {locCtx?.multi && (
            <div className="mb-4">
              <LocationSelect locations={locCtx.locations || []} value={locationId} onChange={setLocationId} required />
            </div>
          )}
          <ItemFields form={form} set={setForm} canPublish={canPublish} />
          <GoldAction type="submit" full variant="primary" data-testid="save-item-btn" disabled={saving || analyzing || uploading} className="mt-6">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Speichern
          </GoldAction>
        </div>
      </form>
      <p className="fundio-note mt-4"><strong>Du behältst die Kontrolle.</strong> Prüfe alle Angaben vor dem Speichern.</p>
    </CockpitLayout>
  );
}
